import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import { useModal } from '../contexts/ModalContext';
import { Helmet } from 'react-helmet-async';

const ContactPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { showAlert } = useModal();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    subject: 'General Support',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/xjkoykkd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          organization: '',
          subject: 'General Support',
          message: ''
        });
      } else {
        await showAlert('There was an error sending your message. Please try again.', 'Error');
      }
    } catch (error) {
      await showAlert('There was an error sending your message. Please try again.', 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <Helmet>
        <title>Contact Us | TeAIch Suite</title>
        <meta name="description" content="Get in touch with the TeAIch team. We typically respond within 24 hours. Enquire about school partnerships, technical support, or volume pricing." />
        <link rel="canonical" href="https://teaich.app/contact" />
      </Helmet>
      <PublicNavBar />
      
      {/* Main Content */}
      <div className="py-8 sm:py-12 md:py-16 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Page Title */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-coral-500 mb-2 sm:mb-4 px-2 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              CONTACT US
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-ink-900 opacity-80 px-2">
              We're here to help you succeed
            </p>
          </div>
          
          {/* Contact Form */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 mb-8 sm:mb-12 md:mb-16">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-ink-900 text-center mb-6 sm:mb-8">
              SEND US A MESSAGE
            </h3>
            
            <form 
              onSubmit={handleSubmit}
              className="max-w-2xl mx-auto space-y-4 sm:space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">FIRST NAME</label>
                  <input 
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                    placeholder="Your first name" 
                  />
                </div>
                <div>
                  <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">LAST NAME</label>
                  <input 
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                    placeholder="Your last name" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">EMAIL ADDRESS</label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="your.email@school.com" 
                />
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">SCHOOL/ORGANIZATION</label>
                <input 
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="Your school or organization" 
                />
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">SUBJECT</label>
                <select 
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base"
                >
                  <option>General Support</option>
                  <option>Technical Issue</option>
                  <option>Billing Question</option>
                  <option>School Partnership</option>
                  <option>Feature Request</option>
                  <option>Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-ink-700 font-bold mb-2 text-sm sm:text-base">MESSAGE</label>
                <textarea 
                  rows="4"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-sm sm:text-base" 
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>
              
              <div className="text-center">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="bg-coral-300 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-base sm:text-lg hover:bg-coral-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>

          {/* Success Popup */}
          {showSuccess && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
              <div className="bg-white rounded-xl border-4 border-ink-900 p-6 max-w-md w-full text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl">✓</span>
                </div>
                <h3 className="text-2xl font-bold text-ink-900 mb-2">Message Sent!</h3>
                <p className="text-ink-600 mb-4">
                  Thanks for reaching out! We'll get back to you within 24 hours.
                </p>
                <button 
                  onClick={() => setShowSuccess(false)}
                  className="bg-coral-300 text-white px-6 py-2 rounded-lg font-bold hover:bg-coral-500 transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-4 border-ink-900 p-4 sm:p-6 md:p-8 text-center">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-ink-900 mb-3 sm:mb-4">RESPONSE TIME</h3>
            <p className="text-ink-600 text-sm sm:text-base md:text-lg">
              We typically respond to all inquiries within <span className="font-bold text-coral-600">24 hours</span> during business days.
            </p>
          </div>

        </div>
      </div>

      <footer className="bg-sand-100 border-t-4 border-coral-300 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Mobile: stacked, Desktop: single row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            
            {/* Logo */}
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 w-auto" />
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a href="YOUR_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/teaich_app/" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="YOUR_TIKTOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.56V6.81a4.85 4.85 0 01-1.07-.12z"/>
                </svg>
              </a>
              <a href="YOUR_LINKEDIN_URL_HERE" target="_blank" rel="noopener noreferrer"
                className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>

            {/* Nav Links */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/feedback')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Feedback
              </button>
              <button onClick={() => navigate('/legal/privacy-policy')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Privacy Policy
              </button>
              <button onClick={() => navigate('/legal/terms-of-service')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Terms of Service
              </button>
              <button onClick={() => navigate('/legal/data-retention')}
                className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">
                Data Retention
              </button>
            </div>

          </div>
        </div>
      </footer>
    </div>  
  );        
};

export default ContactPage;